/**
 * WordPress dependencies
 */
import { __, sprintf } from '@wordpress/i18n';
import {
	Modal,
	Button,
	TabPanel,
	SelectControl,
	Tooltip,
	Icon,
	Spinner,
	Card,
	CardHeader,
	CardBody,
} from '@wordpress/components';
import {
	useSelect,
	useDispatch,
	withSelect,
	withDispatch,
} from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';
import apiFetch from '@wordpress/api-fetch';
import { useState } from '@wordpress/element';
import { compose } from '@wordpress/compose';
import { autop } from '@wordpress/autop';

/**
 * External dependencies.
 */
import classnames from 'classnames';
import { has } from 'lodash';

/**
 * Internal dependencies.
 */
import AlertKitApply from './alert-kit-apply.js';
import styling from '../site-options/inline-css';

const KitDetails = ( { kit, onClose, applyKit } ) => {
	const [ { isAlertKitApply }, setState ] = useState( {
		isAlertKitApply: false,
	} );

	const updateState = ( data ) => {
		setState( ( prevState ) => {
			return {
				...prevState,
				...data,
			};
		} );
	};
	return <>
		<Modal
			className={ classnames(
				'gutenify-plugin-templates-modal',
				'gutenify-component-modal-hide-header'
			) }
			position="top"
			size="lg"
			onRequestClose={ () => {
				onClose();
			} }
			shouldCloseOnClickOutside={ false }
			// icon={ getIcon( 'plugin-templates' ) }
			// isFullScreen={ true }
			__experimentalHideHeader={ true }
		>
			{ kit && <>
				<div className="components-modal__header">
					<div className="components-modal__header-heading-container">
						<h1
							id="components-modal-header-1"
							className="components-modal__header-heading"
						>
							{ __( 'Kit Details' ) }
						</h1>
					</div>

					<button
						type="button"
						aria-label="Close dialog"
						className="components-button components-icon-button"
						onClick={ () => {
							onClose();
						} }
					>
						<svg
							aria-hidden="true"
							role="img"
							focusable="false"
							className="dashicon dashicons-no-alt"
							xmlns="http://www.w3.org/2000/svg"
							width="20"
							height="20"
							viewBox="0 0 20 20"
						>
							<path d="M14.95 6.46L11.41 10l3.54 3.54-1.41 1.41L10 11.42l-3.53 3.53-1.42-1.42L8.58 10 5.05 6.47l1.42-1.42L10 8.58l3.54-3.53z" />
						</svg>
					</button>
				</div>
				<Card>
					<CardHeader>
						<div>
							<h1 className="gutenify-kit-title">
								{ kit.title }
								{ !! kit.is_pro ? <span className="gutneify-kit-type-label gutneify-kit-type-label-pro">PRO</span> : <span className="gutneify-kit-type-label gutneify-kit-type-label-free">FREE</span> }
							</h1>
						</div>

						<div className="gutenify-template-browser-button-group">
							{ kit.demo_link && (
								<Button
									isSecondary
									href={ kit.demo_link }
									target="__blank"
								>
									<Icon icon="welcome-view-site" />
									View Demo
								</Button>
							) }
							<Button
								isPrimary
								onClick={ async () => {
									updateState( {
										isAlertKitApply: true,
									} );
								} }
								disabled={ !! kit.is_pro && '' === window._gutenify_kit.pro_license_status }
							>
								<Icon icon="yes" />
								Apply Kit
							</Button>

							{ isAlertKitApply && (
								<AlertKitApply
									onCancel={ () => {
										updateState( {
											isAlertKitApply: false,
										} );
									} }
									onApply={ async ( importOptions ) => {
										return await applyKit(
											kit,
											importOptions
										);
									} }
									kit={ kit }
								/>
							) }
						</div>
					</CardHeader>
					<CardBody>
						<div className="gutenify-template-kit-information-wrapper">
							<div
								dangerouslySetInnerHTML={ {
									__html: autop( kit.content ),
								} }
								className="gutenify-template-kit-information-content"
							/>
							<div className="gutenify-template-kit-information-sidebar">
								<h3>{ __( 'Kit includes following:' ) }</h3>
								{ kit.kit_parts.length > 0 && (
									<div>
										<h4>Template Parts</h4>
										<ul>
											{ kit.kit_parts.map( ( item ) => {
												const hasDemo = !! item.demo_link;
												return (
													<li key={ `part-${ kit.name }-${ item.slug }` }>
														<Icon icon="yes" />{ ' ' }
														{ hasDemo ? (
															<a
																href={ item.demo_link }
																target="__blank"
															>
																{ item.title }
															</a>
														) : (
															<span>{ item.title }</span>
														) }
													</li>
												);
											} ) }
										</ul>
									</div>
								) }

								{ kit.kit_templates.length > 0 && (
									<div>
										<h4>Templates</h4>
										<ul>
											{ kit.kit_templates.map( ( item ) => {
												const hasDemo = !! item.demo_link;
												return (
													<li key={ `templates-${ kit.name }-${ item.slug }` }>
														<Icon icon="yes" />{ ' ' }
														{ hasDemo ? (
															<a
																href={ item.demo_link }
																target="__blank"
															>
																{ item.title }
															</a>
														) : (
															<span>{ item.title }</span>
														) }
													</li>
												);
											} ) }
										</ul>
									</div>
								) }

								{ kit.kit_demo_pages.length > 0 && (
									<div>
										<h4>Demo Pages</h4>
										<ul>
											{ kit.kit_demo_pages.map( ( item ) => {
												const hasDemo = !! item.demo_link;
												return (
													<li key={ `pages-${ kit.name }-${ item.slug }` }>
														<Icon icon="yes" />{ ' ' }
														{ hasDemo ? (
															<a
																href={ item.demo_link }
																target="__blank"
															>
																{ item.title }
															</a>
														) : (
															<span>{ item.title }</span>
														) }
													</li>
												);
											} ) }
										</ul>
									</div>
								) }
								{ ( kit.kit_core_settings || kit.kit_gutenify_settings ) && (
									<div>
										<h4>Style & Settings</h4>
										<ul>
											<li>
												<Icon icon="yes" /> Custom Settings & styles
											</li>
										</ul>
									</div>
								) }
								{ kit.required_plugins.length > 0 && (
									<div>
										<h4>Required Plugins</h4>
										<ul>
											{ kit.required_plugins.map( ( item, index ) => {
												const hasURL = !! item.plugin_url;
												return (
													<li key={ `plugins-${ kit.name }-${ index }` }>
														<Icon icon="arrow-right" />{ ' ' }
														{ hasURL ? (
															<a
																href={ item.plugin_url }
																target="__blank"
															>
																{ item.plugin_name }
															</a>
														) : (
															<span>{ item.plugin_name }</span>
														) }
													</li>
												);
											} ) }
										</ul>
									</div>
								) }
							</div>
						</div>
					</CardBody>
				</Card>
			</> }
		</Modal>
	</>;
};

export default compose( [
	withSelect( ( select ) => {
		const styleId = select( coreStore ).__experimentalGetCurrentGlobalStylesId();
		return {
			styleId,
		};
	} ),
	withDispatch( ( dispatch, props ) => {
		const { styleId } = props;
		const applyKit = ( selectedKit, importOptions ) => {
			const {
				activeParts,
				activeTemplates,
				activeDemoPages,
				activeGutenifySettings,
				activeCoreSettings,
				activeStaticHomepage,
			} = importOptions;
			return new Promise( function( resolve, reject ) {
				apiFetch( {
					path: `/gutenify/v1/get_template_kit_data?id=${ selectedKit.id }&type=remote`,
				} )
					.then( ( response ) => {
						const kit = response.response;

						const kitNavigations = kit.kit_navigations ? kit.kit_navigations : [];

						let kitParts = [];
						if ( kit.kit_parts ) {
							kitParts = kit.kit_parts.filter( ( item ) => {
								return activeParts.includes( item.slug );
							} );
						}

						let kitTemplates = [];
						if ( kit.kit_parts ) {
							kitTemplates = kit.kit_templates.filter( ( item ) => {
								return activeTemplates.includes( item.slug );
							} );
						}

						let kitDemoPages = [];
						if ( kit.kit_parts ) {
							kitDemoPages = kit.kit_demo_pages.filter( ( item ) => {
								return activeDemoPages.includes( item.slug );
							} );
						}

						apiFetch( {
							path: `/gutenify/v1/create_kit`,
							method: 'POST',
							data: {
								title: kit.title,
								name: kit.kit_name,
								template_parts: kitParts,
								templates: kitTemplates,
								demo_pages: kitDemoPages,
								navigations: kitNavigations,
								styleId,
								staticHomePage: activeStaticHomepage ? kit.static_home_page_slug : false,
							},
						} )
							.then( () => {
								if ( activeCoreSettings && has( kit, `kit_core_settings.styles` ) ) {
									apiFetch( {
										path: `/wp/v2/global-styles/${ styleId }`,
										method: 'POST',
										data: {
											styles: kit.kit_core_settings.styles,
											settings: kit.kit_core_settings.settings,
										},
									} )
										.then( () => {
											// resolve( true );
										} )
										.catch( () => {
											// reject( false );
										} );
								}

								if ( activeGutenifySettings && has( kit, `kit_gutenify_settings.styles` ) ) {
									apiFetch( {
										path: `/gutenify/v1/update_site_options`,
										method: 'POST',
										data: {
											styles: kit.kit_gutenify_settings.styles,
										},
									} )
										.then( ( ) => {
											// resolve( true );
										} )
										.catch( ( ) => {
										} );
								}

								const coreStyles = has( kit, `kit_core_settings.styles` ) ? kit.kit_core_settings.styles : {};
								const gutenifyStyles = has( kit, `kit_gutenify_settings.styles` ) ? kit.kit_gutenify_settings.styles : {};
								const css = styling( coreStyles, gutenifyStyles );
								const adminCss = styling( coreStyles, gutenifyStyles, true );
								apiFetch( {
									path: `/gutenify/v1/update_global_styles`,
									method: 'POST',
									data: {
										css,
										adminCss,
									},
								} )
									.then( ( ) => {
										// resolve( true );
									} )
									.catch( ( ) => {
										// reject( false );
									} );

								resolve( true );
							} )
							.catch( () => {
								reject( false );
							} );
					} )
					.catch( () => {
						reject( false );
					} );
			} );
		};

		return {
			applyKit,
		};
	} ),
] )( KitDetails );
